// ============================================================
// shared/decoupe.js — le contrat d'une découpe de plan (14 octobre 2026).
// ------------------------------------------------------------
// Partagé : l'éditeur valide AVANT d'envoyer, le serveur revalide en
// recevant. Mêmes règles des deux côtés, une seule écriture.
//
// Les bornes viennent d'AGC (au moins une zone, coordonnées 0–1, pas de
// zone de moins de 0,5 % de côté, pas de débordement au-delà d'une
// tolérance d'arrondi), plus trois règles propres à Mertens : au moins
// une zone pièce, au plus un cartouche par page, au plus vingt pièces —
// et une seule pièce sans cartouche ne se découpe pas : ce serait
// recadrer, pas découper.
// ============================================================

import { z } from 'zod';

export const MAX_ZONES_PIECE = 20;
export const TYPES_ZONE = ['piece', 'cartouche'];

const coordonnee = z.number().finite().min(0).max(1);

export const zoneDecoupeSchema = z.object({
  type: z.enum(TYPES_ZONE),
  libelle: z.string().trim().max(80).optional().nullable(),
  x: coordonnee,
  y: coordonnee,
  w: coordonnee,
  h: coordonnee,
}).strict();

export const decoupeSchema = z.object({
  zones: z.array(zoneDecoupeSchema).min(1).max(60),
  // La consigne de la pièce remplacée, recopiée sur chaque zone. Décochée
  // par défaut (décision de Thibaud, 14/10) : sur 2026-10-013 elle disait
  // « 2 pièces assemblées sur le plan » — la recopier sur chacune serait faux.
  recopier_consigne: z.boolean().optional().default(false),
  // Le geste qui accepte de perdre le travail fait sur la pièce remplacée.
  // Sans lui, le serveur refuse et DIT ce qui serait perdu.
  confirmer_perte: z.boolean().optional().default(false),
}).strict().superRefine((corps, ctx) => {
  for (const msg of problemesDecoupe(corps.zones)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path: ['zones'] });
  }
});

/**
 * Ce qui ne va pas dans un jeu de zones, en phrases. Vide si tout va bien.
 * L'éditeur s'en sert pour désactiver « Créer les pièces » en disant
 * pourquoi ; le serveur, pour refuser en 400 avec le même message.
 */
export function problemesDecoupe(zones = []) {
  const problemes = [];
  zones.forEach((z0, i) => {
    const n = z0.libelle || `zone ${i + 1}`;
    if (!(z0.w > 0.005) || !(z0.h > 0.005)) problemes.push(`La ${n} est trop petite.`);
    if (z0.x + z0.w > 1.001 || z0.y + z0.h > 1.001) problemes.push(`La ${n} sort de la page.`);
  });
  const pieces = zones.filter((z0) => z0.type === 'piece').length;
  const cartouches = zones.filter((z0) => z0.type === 'cartouche').length;
  if (pieces === 0) problemes.push('Tracez au moins une pièce.');
  if (pieces > MAX_ZONES_PIECE) problemes.push(`Au plus ${MAX_ZONES_PIECE} pièces par page.`);
  if (cartouches > 1) problemes.push('Un seul cartouche par page.');
  if (pieces === 1 && cartouches === 0) {
    problemes.push('Une seule pièce sans cartouche : il n’y a rien à découper.');
  }
  return problemes;
}
